import { useMutation } from "react-query";
import { strings, COMPANY_CREATION_URL } from "../../constants";
import { sendTag } from "../../analytics";

type CreateOrderInput = {
  order: OrderRequest;
  setCompanyAlreadyExist: (companyExists: boolean) => void;
};

type OrderRequest = {
  data: string;
};

export class OrderCreationError extends Error {
  status: number;
  errorCode?: string;
  backendStatus?: number;
  backendErrorCode?: string;

  constructor(
    message: string,
    status: number,
    errorCode?: string,
    backendStatus?: number,
    backendErrorCode?: string
  ) {
    super(message);
    // Restore the prototype chain: when TS transpiles `extends Error` to an ES5 target,
    // `super(message)` returns an object whose proto is Error.prototype, so `instanceof
    // OrderCreationError` returns false. Without this, downstream callers fall back to
    // the generic-Error branch and the structured fields below are invisible to them.
    Object.setPrototypeOf(this, OrderCreationError.prototype);
    this.name = "OrderCreationError";
    this.status = status;
    this.errorCode = errorCode;
    this.backendStatus = backendStatus;
    this.backendErrorCode = backendErrorCode;
  }
}

export function useCreateOrder() {
  return useMutation(async (createOrderInput: CreateOrderInput) => {
    const orderPath = `${COMPANY_CREATION_URL}/v1/orders`;

    const response = await fetch(orderPath, {
      method: "POST",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(createOrderInput.order),
    });

    // Read body once as text so a non-JSON / empty-body response (e.g. a 401
    // from the auth middleware) doesn't throw an opaque SyntaxError on parse.
    const bodyText = await response.text();
    let data: any = null;
    try {
      data = bodyText ? JSON.parse(bodyText) : null;
    } catch {
      // leave data null; the status code carries enough signal for callers
    }

    if (!response.ok) {
      if (data?.error === "CompanyAlreadyExists") {
        sendTag({ status: "companyAlreadyExists" });
        createOrderInput.setCompanyAlreadyExist(true);
        return;
      }
      throw new OrderCreationError(
        `Order POST failed (${response.status})`,
        response.status,
        typeof data?.error === "string" ? data.error : undefined,
        typeof data?.backendStatus === "number" ? data.backendStatus : undefined,
        typeof data?.backendErrorCode === "string" ? data.backendErrorCode : undefined
      );
    }

    if (!data?.identifier) {
      throw new OrderCreationError(
        "Order POST succeeded but response had no identifier",
        response.status
      );
    }

    sessionStorage.setItem(strings.ORDER_ID_COOKIE_NAME, data.identifier);
    return data;
  });
}
